import type { TideDatum, TideDay, TideEvent, TideKind, TidePosition, TideSource, TideSummary } from "./types";
import { formatInStoneHarbor, localDateKey, STONE_HARBOR } from "./stoneHarbor";

type RawNoaaPrediction = {
  t: string;
  v?: string;
  type: "H" | "L";
};

type TideSummaryOptions = {
  source?: TideSource;
  stationName?: string;
  stationId?: string;
  datum?: TideDatum;
  message?: string;
};

export function parseNoaaPredictions(predictions: RawNoaaPrediction[]): TideEvent[] {
  return predictions
    .map((prediction) => {
      const iso = prediction.t.includes("T")
        ? prediction.t
        : prediction.t.replace(" ", "T") + ":00-04:00";
      const kind: TideKind = prediction.type === "H" ? "high" : "low";
      return {
        id: `${kind}-${iso}`,
        kind,
        time: iso,
        heightFt: prediction.v === undefined ? undefined : Number(prediction.v)
      };
    })
    .sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
}

export function groupTidesByDay(events: TideEvent[], now = new Date()): TideDay[] {
  const todayKey = localDateKey(now.toISOString());
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = localDateKey(tomorrow.toISOString());
  const groups = new Map<string, TideEvent[]>();

  for (const event of events) {
    const key = localDateKey(event.time);
    const list = groups.get(key) || [];
    list.push(event);
    groups.set(key, list);
  }

  return Array.from(groups.entries()).map(([date, dayEvents]) => ({
    date,
    label: date === todayKey ? "Today" : date === tomorrowKey ? "Tomorrow" : formatInStoneHarbor(dayEvents[0].time, { weekday: "long" }),
    events: dayEvents
  }));
}

export function summarizeTides(events: TideEvent[], now = new Date(), options: TideSummaryOptions = {}): TideSummary {
  const upcoming = events.filter((event) => Date.parse(event.time) >= now.getTime());
  const previous = [...events].reverse().find((event) => Date.parse(event.time) < now.getTime());
  const next = upcoming[0];
  const nextHigh = upcoming.find((event) => event.kind === "high");
  const nextLow = upcoming.find((event) => event.kind === "low");
  const current = calculateTidePosition(previous, next, now);

  let state: TideSummary["state"] = "unknown";
  if (previous && next) {
    if (previous.kind === "low" && next.kind === "high") state = "rising";
    else if (previous.kind === "high" && next.kind === "low") state = "falling";
    else state = "slack";
  }

  return {
    state,
    next,
    nextHigh,
    nextLow,
    current,
    days: groupTidesByDay(upcoming, now).slice(0, 5),
    stationName: options.stationName || STONE_HARBOR.tideStationName,
    stationId: options.stationId || STONE_HARBOR.tideStationId,
    source: options.source || "noaa-coops",
    datum: options.datum || "MLLW",
    generatedAt: now.toISOString(),
    availability: "live",
    lastSuccessfulAt: now.toISOString(),
    message: options.message
  };
}

export function unavailableTides(now = new Date(), message = "Tide predictions are temporarily unavailable."): TideSummary {
  return {
    state: "unknown",
    days: [],
    stationName: STONE_HARBOR.tideStationName,
    stationId: STONE_HARBOR.tideStationId,
    source: "none",
    datum: "unknown",
    generatedAt: now.toISOString(),
    availability: "unavailable",
    message
  };
}

export function tideKindLabel(kind: TideKind): string {
  return kind === "high" ? "High tide" : "Low tide";
}

function calculateTidePosition(previous: TideEvent | undefined, next: TideEvent | undefined, now: Date): TidePosition | undefined {
  if (!previous || !next) return undefined;
  const previousTime = Date.parse(previous.time);
  const nextTime = Date.parse(next.time);
  const nowTime = now.getTime();
  if (!Number.isFinite(previousTime) || !Number.isFinite(nextTime) || nextTime <= previousTime) {
    return undefined;
  }

  const progressPercent = clampPercent(Math.round(((nowTime - previousTime) / (nextTime - previousTime)) * 100));
  const percent = previous.kind === "high" && next.kind === "low"
    ? 100 - progressPercent
    : previous.kind === "low" && next.kind === "high"
      ? progressPercent
      : next.kind === "high"
        ? 100
        : 0;

  return {
    percent: clampPercent(percent),
    progressPercent,
    label: `${progressPercent}% toward ${tideKindLabel(next.kind).toLowerCase()}`
  };
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}
